import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { message } from 'antd'
import { useAuthStore } from '@/store/auth'
import type { Capabilities } from '@/store/capabilities'
import { useCapabilities } from '@/hooks/useCapabilities'
import { useCapabilitiesStore } from '@/store/capabilities'

type UserRole = 'superadmin' | 'org_admin' | 'user' | 'analyst' | 'viewer'

interface ProtectedRouteProps {
  children: ReactNode
  /** If set, only these roles can access the route */
  requiredRoles?: UserRole[]
  /** If set, this capability grants access regardless of legacy role. */
  requiredCapability?: keyof Capabilities
  /** If set, the whole route subtree requires adm portal access. */
  requireAdminPortal?: boolean
}

function ProtectedRoute({
  children,
  requiredRoles,
  requiredCapability,
  requireAdminPortal = false,
}: ProtectedRouteProps) {
  const token = useAuthStore((state) => state.token)
  const user = useAuthStore((state) => state.user)
  const location = useLocation()
  const capabilities = useCapabilities()
  const capabilitiesResolved = useCapabilitiesStore((state) => state.isResolved)

  // Not authenticated — redirect to login
  if (!token) {
    return <Navigate to="/login" replace />
  }

  // User not loaded yet — wait (fetchUser is called in CallbackPage)
  if (!user) {
    return null
  }

  const isAdminRoute = location.pathname.startsWith('/admin')
  const needsAdminPortal = requireAdminPortal || isAdminRoute

  // Capabilities for admin routes are fetched asynchronously after session
  // restoration. Wait for that resolution to avoid false redirects on cold load.
  if (needsAdminPortal && !capabilitiesResolved) {
    return null
  }

  if (needsAdminPortal && !capabilities.adminAccess) {
    return <AdminPortalDeniedRedirect />
  }

  const hasCapabilityAccess = requiredCapability
    ? Boolean(capabilities[requiredCapability])
    : true
  const hasLegacyRoleAccess = requiredRoles
    ? requiredRoles.includes(user.role as UserRole)
    : true
  const hasAccess = requiredCapability ? hasCapabilityAccess : hasLegacyRoleAccess

  // Check admin route access via capabilities
  const hasAdminAccess = !isAdminRoute || capabilities.adminAccess

  if (!hasAccess || !hasAdminAccess) {
    return <UnauthorizedRedirect />
  }

  return <>{children}</>
}

/**
 * Shows notification and redirects to home.
 * Separated into component to use useEffect for the notification.
 */
function UnauthorizedRedirect() {
  useEffect(() => {
    message.warning('Недостаточно прав для доступа к этой странице')
  }, [])

  return <Navigate to="/" replace />
}

function AdminPortalDeniedRedirect() {
  useEffect(() => {
    useCapabilitiesStore.getState().resetCapabilities()
    useAuthStore.getState().clearAuth()
    message.warning('У вашей учетной записи нет доступа к административному порталу.')
  }, [])

  return <Navigate to="/login?error=admin_access_required" replace />
}

export default ProtectedRoute
